<script setup lang="ts">
import DatePicker from 'primevue/datepicker';
import { computed, ref, useAttrs, useSlots, watch } from 'vue';
import DateTimePickerTimeControls from './DateTimePickerTimeControls.vue';
import {
  applyTimePartsToDate,
  readTimePartsFromDate,
  sameDateInstant,
  type DateTimePickerHourFormat,
} from '../utils/datetime-picker-time';

/**
 * Thin wrapper around PrimeVue DatePicker: same props, events, and slots as DatePicker,
 * except #buttonbar — replaced with manual hour/minute (+ AM/PM for 12h).
 * Built-in overlay time spinners are hidden; `showTime` is always enabled so the input shows date + time.
 *
 * For `selectionMode="range"` with multiple months, use {@link GpxDateTimeRangePicker}.
 */
defineOptions({ inheritAttrs: false });

const model = defineModel<Date | null>({ required: false });

const attrs = useAttrs();
const slots = useSlots();

const forwardedSlotNames = computed(() =>
  Object.keys(slots).filter((name) => name !== 'buttonbar'),
);

const GPX_DATETIME_PANEL_CLASS = 'gpx-datetime-picker-panel';

const pickerAttrs = computed(() => {
  const {
    class: _class,
    panelClass: _panelClass,
    'panel-class': _panelClassKebab,
    modelValue: _modelValue,
    'onUpdate:modelValue': _onUpdateModelValue,
    onShow: _onShow,
    onHide: _onHide,
    onClearClick: _onClearClick,
    ...rest
  } = attrs;

  return rest;
});

/** Overlay is portaled to `body` by default — panelClass scopes global hide rules. */
const panelClass = computed(() => [
  GPX_DATETIME_PANEL_CLASS,
  attrs.panelClass ?? attrs['panel-class'],
]);

const hourFormat = computed((): DateTimePickerHourFormat => {
  const raw = attrs.hourFormat ?? attrs['hour-format'];
  return raw === '24' ? '24' : '12';
});

const disabled = computed(() => Boolean(attrs.disabled));
const size = computed(
  () => attrs.size as 'small' | 'large' | undefined,
);

const hour = ref(12);
const minute = ref(0);
const pm = ref(false);

let syncingFromModel = false;

function syncTimePartsFromModel() {
  const parts = readTimePartsFromDate(model.value, hourFormat.value);
  hour.value = parts.hour;
  minute.value = parts.minute;
  pm.value = parts.pm;
}

function publishMerged(base: Date | null | undefined) {
  const next = applyTimePartsToDate(
    base,
    { hour: hour.value, minute: minute.value, pm: pm.value },
    hourFormat.value,
  );
  if (sameDateInstant(next, model.value)) {
    return;
  }
  model.value = next;
}

watch(
  () => model.value,
  () => {
    syncingFromModel = true;
    syncTimePartsFromModel();
    syncingFromModel = false;
  },
  { immediate: true },
);

watch([hour, minute, pm], () => {
  if (syncingFromModel) {
    return;
  }
  publishMerged(model.value);
});

watch(hourFormat, () => {
  syncTimePartsFromModel();
});

function onCalendarUpdate(
  value: Date | Date[] | (Date | null)[] | null | undefined,
) {
  if (Array.isArray(value)) {
    return;
  }
  if (value == null) {
    model.value = null;
    return;
  }
  publishMerged(value);
}

function invokeAttrListener(key: 'onShow' | 'onHide' | 'onClearClick') {
  const handler = attrs[key];
  if (typeof handler === 'function') {
    handler();
  }
}

function onShow() {
  syncTimePartsFromModel();
  invokeAttrListener('onShow');
}

function onHide() {
  invokeAttrListener('onHide');
}

function onClearClick() {
  invokeAttrListener('onClearClick');
}
</script>

<template>
  <DatePicker
    v-bind="pickerAttrs"
    :model-value="model"
    class="gpx-datetime-picker"
    :class="attrs.class"
    :panel-class="panelClass"
    show-time
    show-button-bar
    @update:model-value="onCalendarUpdate"
    @show="onShow"
    @hide="onHide"
    @clear-click="onClearClick"
  >
    <template
      v-for="name in forwardedSlotNames"
      :key="name"
      #[name]="slotProps"
    >
      <slot :name="name" v-bind="slotProps ?? {}" />
    </template>

    <template #buttonbar>
      <div class="flex w-full justify-center">
        <DateTimePickerTimeControls
          v-model:hour="hour"
          v-model:minute="minute"
          v-model:pm="pm"
          :hour-format="hourFormat"
          :disabled="disabled"
          :size="size"
        />
      </div>
    </template>
  </DatePicker>
</template>

<!-- Panel is teleported to body; scoped :deep() does not reach it. -->
<style>
.gpx-datetime-picker-panel .p-datepicker-time-picker {
  display: none !important;
}
</style>
